import React, { useState, useMemo } from "react";
import {
  EmployeeProfile,
  EmployeeRecord,
  formatDate3,
  getDefaultUserAvatar,
} from "winslow-core";
import { useValidatedImageUrl } from "../../hooks/useValidatedImageUrl";
import { CopyTooltip } from "../common/CopyTooltip";
import { erGetCPValue } from "../app/app-employee-records/employeeRecordUtils";
import "./EmployeeProfileContent.css";

export interface EmployeeProfileContentProps {
  employee?: EmployeeProfile | null;
  employeeRecord?: EmployeeRecord | null;
  onEmailCopy?: (email: string) => void;
  className?: string;
}

export const EmployeeProfileContent: React.FC<EmployeeProfileContentProps> = ({
  employee,
  employeeRecord,
  onEmailCopy,
  className = "",
}) => {
  const displayData = useMemo(() => {
    if (employeeRecord) {
      const firstName = erGetCPValue(employeeRecord, "system-firstName") || "";
      const lastName = erGetCPValue(employeeRecord, "system-lastName") || "";
      const title = erGetCPValue(employeeRecord, "system-title");
      const displayName =
        [firstName, lastName].filter(Boolean).join(" ").trim() ||
        employeeRecord.workEmail ||
        "Employee";

      return {
        displayName,
        email: employeeRecord.workEmail,
        avatar: employeeRecord.avatarURL || "",
        title: title || undefined,
      };
    } else if (employee) {
      return {
        displayName: employee.displayName || employee.email,
        email: employee.email,
        avatar: employee.avatar || "",
        title: employee.title,
      };
    }
    return {
      displayName: "Unknown Employee",
      email: "",
      avatar: "",
      title: undefined,
    };
  }, [employee, employeeRecord]);

  const fallbackAvatar = getDefaultUserAvatar(displayData.displayName);
  const { validatedUrl, isValidating } = useValidatedImageUrl(
    displayData.avatar,
    fallbackAvatar
  );

  const [emailCopied, setEmailCopied] = useState(false);

  const handleEmailCopy = (email: string) => {
    navigator.clipboard.writeText(email);
    setEmailCopied(true);
    setTimeout(() => setEmailCopied(false), 3000);
    if (onEmailCopy) {
      onEmailCopy(email);
    }
  };

  // Parse custom properties from EmployeeRecord
  const customProperties = useMemo(() => {
    if (!employeeRecord || !employeeRecord.customProperties) return [];

    const properties: Array<{
      label: string;
      value: any;
      icon: string;
    }> = [];

    // Known field mappings for better labels and icons
    const fieldMappings: Record<string, { label: string; icon: string }> = {
      "system-firstName": { label: "First Name", icon: "/icons/person.svg" },
      "system-lastName": { label: "Last Name", icon: "/icons/person.svg" },
      "system-title": { label: "Title", icon: "/icons/briefcase.svg" },
      "system-department": {
        label: "Department",
        icon: "/icons/briefcase.svg",
      },
      "system-address": { label: "Location", icon: "/icons/location.svg" },
      "system-office": { label: "Office", icon: "/icons/office.svg" },
      "system-country": { label: "Country", icon: "/icons/country.svg" },
      "system-startDate": { label: "Start Date", icon: "/icons/calendar.svg" },
      "system-birthDate": { label: "Birth Date", icon: "/icons/calendar.svg" },
      "system-managerName": { label: "Manager", icon: "/icons/person.svg" },
      "system-managerId": { label: "Manager", icon: "/icons/person.svg" },
      "system-personalEmail": {
        label: "Personal Email",
        icon: "/icons/mail.svg",
      },
      "system-personalPhone": {
        label: "Personal Phone",
        icon: "/icons/phone.svg",
      },
      "system-salaryType": { label: "Salary Type", icon: "/icons/dollar.svg" },
      "system-salaryRate": { label: "Salary Rate", icon: "/icons/dollar.svg" },
      "system-designation": { label: "Designation", icon: "/icons/star.svg" },
      "system-daysOfWork": {
        label: "Days of Work",
        icon: "/icons/calendar.svg",
      },
    };

    // Skip fields already shown in the header
    const skipFields = ["system-firstName", "system-lastName", "system-title"];

    // Parse the customProperties JSON string
    let customProps: Array<{ label: string; data: any; value?: any }> = [];
    try {
      customProps =
        typeof employeeRecord.customProperties === "string"
          ? JSON.parse(employeeRecord.customProperties)
          : employeeRecord.customProperties || [];
    } catch (e) {
      console.error("Failed to parse custom properties:", e);
      return [];
    }

    // Iterate through all custom properties
    customProps.forEach((prop) => {
      const key = prop.label;

      // Skip fields shown in header
      if (skipFields.includes(key)) return;

      // Get the value (prefer 'data' over 'value' field)
      const value = prop.data !== undefined ? prop.data : prop.value;

      // Skip empty values
      if (value === null || value === undefined || value === "") return;

      // Get label and icon (use mapping if available, otherwise generate from key)
      const mapping = fieldMappings[key];
      const label = mapping
        ? mapping.label
        : key
            .replace(/^system-/, "")
            .replace(/([A-Z])/g, " $1")
            .replace(/^./, (str) => str.toUpperCase());
      const icon = mapping ? mapping.icon : "/icons/info.svg";

      // Format the value
      let formattedValue = value;
      if (Array.isArray(value)) {
        formattedValue = value.join(", ");
      } else if (typeof value === "object" && value !== null) {
        formattedValue = JSON.stringify(value);
      }

      properties.push({
        label,
        value: formattedValue,
        icon,
      });
    });

    return properties;
  }, [employeeRecord]);

  return (
    <div className={`employee-profile-content ${className}`}>
      {/* Hero Section */}
      <div className="employee-profile-hero">
        <div className="employee-profile-hero-background" />
        <div className="employee-profile-hero-content">
          {/* Avatar */}
          <div className="employee-profile-avatar-container">
            <div className="employee-profile-avatar-background">
              {isValidating ? (
                <div className="employee-profile-avatar-loading">
                  <div className="loading-spinner" />
                </div>
              ) : (
                <img
                  className="employee-profile-avatar"
                  src={validatedUrl}
                  alt={displayData.displayName}
                />
              )}
            </div>
          </div>

          {/* Name and Title */}
          <div className="employee-profile-name">
            {displayData.displayName}
          </div>
          {displayData.title && (
            <div className="employee-profile-title">{displayData.title}</div>
          )}

          {/* Email */}
          {displayData.email && (
            <CopyTooltip text={emailCopied ? "Copied!" : "Copy email"}>
              <div
                className="employee-profile-email"
                onClick={() => handleEmailCopy(displayData.email)}>
                <div className="employee-profile-email-content">
                  <img src="/icons/mail.svg" alt="Mail Icon" />
                  <span>{displayData.email}</span>
                </div>
              </div>
            </CopyTooltip>
          )}
        </div>
      </div>

      {/* Details Sections */}
      <div className="employee-profile-details">
        {/* Render dynamic properties from EmployeeRecord if available */}
        {employeeRecord && customProperties.length > 0 ? (
          <div className="employee-profile-properties">
            {customProperties.map((property, index) => (
              <div key={index} className="employee-profile-property-row">
                <div className="employee-profile-property-label">
                  {property.label}
                </div>
                <div className="employee-profile-property-value">
                  {property.value}
                </div>
              </div>
            ))}
          </div>
        ) : employee ? (
          /* Fallback to compact rendering for regular Employee profiles */
          <div className="employee-profile-properties">
            {employee.department && (
              <div className="employee-profile-property-row">
                <div className="employee-profile-property-label">
                  Department
                </div>
                <div className="employee-profile-property-value">
                  {employee.department}
                </div>
              </div>
            )}
            {employee.location && (
              <div className="employee-profile-property-row">
                <div className="employee-profile-property-label">Location</div>
                <div className="employee-profile-property-value">
                  {employee.location}
                </div>
              </div>
            )}
            {employee.office && (
              <div className="employee-profile-property-row">
                <div className="employee-profile-property-label">Office</div>
                <div className="employee-profile-property-value">
                  {employee.office}
                </div>
              </div>
            )}
            {employee.country && (
              <div className="employee-profile-property-row">
                <div className="employee-profile-property-label">Country</div>
                <div className="employee-profile-property-value">
                  {employee.country}
                </div>
              </div>
            )}
            {employee.startDate && (
              <div className="employee-profile-property-row">
                <div className="employee-profile-property-label">
                  Start Date
                </div>
                <div className="employee-profile-property-value">
                  {formatDate3(new Date(employee.startDate), false)}
                </div>
              </div>
            )}
            {employee.managerName && (
              <div className="employee-profile-property-row">
                <div className="employee-profile-property-label">Manager</div>
                <div className="employee-profile-property-value">
                  {employee.managerName}
                </div>
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
};
